<template>
  <div class="card mb-5 mb-xl-10">
    <div class="card-header border-0 cursor-pointer">
      <div class="card-title w-100 m-0">
        <div class="d-flex w-100 align-items-center">
          <h3 class="fw-bolder m-0">Input Media File</h3>
        </div>
      </div>
    </div>
    <el-form :model="form" ref="formRef" label-position="top">
      <div class="card-body">
        <!-- Media Selection Form -->
        <div class="row">
          <div class="col-12 col-md-6">
            <el-form-item 
              label="Nama Media" 
              prop="account" 
              :rules="{
                required: true,
                message: 'Nama Media Wajib Diisi',
                trigger: ['blur', 'change'],
              }"
            >
              <el-select
                filterable
                placeholder="Nama Media"
                :allow-create="mediaOptions.length === 0"
                v-model="form.account"
                reserve-keyword
                :loading="loadingMedia"
                :remote-method="searchMedia"
                autocomplete="off"
                :remote="true"
                class="d-block"
              >
                <el-option
                  v-for="item in mediaOptions"
                  :key="item.id"
                  :label="item.nama"
                  :value="item.nama"
                >
                <span :style="{ color: item._id ? '#409EFF' : '' }">
                          {{ item._id ? item.nama : ` ${item.nama}` }}
                        </span>
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col-12 col-md-6">
            <el-form-item 
              label="Tanggal Media" 
              prop="date" 
              :rules="{
                required: true,
                message: 'Tanggal Media Wajib Diisi',
                trigger: ['blur', 'change'],
              }"
            >
              <el-date-picker 
                v-model="form.date" 
                placeholder="Tanggal Media"
                type="datetime" 
                style="width: 100%"
              />
            </el-form-item>
          </div>
        </div>

        <!-- File Upload Section -->
        <el-form-item label="File Media" prop="media_files">
          <el-upload
            action="#"
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="fileList"
            multiple
            accept="image/*"
            :before-upload="beforeUpload"
            class="w-100 w-md-auto"
          >
            <el-button type="primary" class="w-100 w-md-auto" >Upload File Media</el-button>
            <template #tip>
              <div class="el-upload__tip">
                Maksimal Ukuran: 50 MB, Ekstensi: png, jpg, jpeg.
              </div>
            </template>
          </el-upload>
        </el-form-item>

        <div v-if="fileList.some(f => f.uploadError)" class="mb-3">
          <el-button @click="retryFailedUploads" type="warning" class="w-100 w-md-auto">
            <i class="el-icon-refresh"></i> Ulangi Upload yang Gagal
          </el-button>
        </div>

        <!-- Page List Table -->
        <div v-if="fileList.length > 0" class="mt-5">
          <h4 class="mb-4">Daftar Halaman (Total: {{ fileList.length }})</h4>
          
          <!-- Desktop Table View -->
          <div class="d-none d-md-block table-responsive">
            <table class="table table-bordered table-hover">
              <thead class="table-light">
                <tr>
                  <th style="width: 10%">Halaman</th>
                  <th style="width: 60%" class="text-center">Gambar</th>
                  <th style="width: 20%">Rubrik</th>
                  <th style="width: 10%" class="text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(file, index) in fileList" :key="file.uid">
                  <td class="align-middle text-center">
                    <el-input 
                      v-model="file.page" 
                      placeholder="Contoh: 1, A1, B2"
                    />
                  </td>
                  
                  <td class="align-middle">
                    <div class="d-flex justify-content-center align-items-center">
                      <img 
                        :src="file.url" 
                        :alt="`Gambar ${file.page}`" 
                        class="img-thumbnail cursor-zoom"
                        style="max-height: 100px"
                        @click="openZoomModal(file.url, index)"
                      />
                      <div v-if="file.uploading" class="ms-2">
                        <el-progress 
                          :percentage="file.uploadProgress" 
                          :status="file.uploadError ? 'exception' : ''"
                          :width="80"
                        />
                        <span v-if="file.uploadError" class="text-danger small">
                          {{ file.uploadError }}
                        </span>
                        <el-button v-if="file.uploadError" @click="uploadFile(file)" size="mini" type="danger" plain
                          class="mt-1">
                          <i class="el-icon-refresh"></i> Coba Lagi
                        </el-button>
                      </div>
                    </div>
                  </td>
                  
                  <td class="align-middle">
                    <el-select 
                      v-model="file.rubrik" 
                      placeholder="Pilih Rubrik"
                      filterable
                      style="width: 100%"
                      :loading="loadingRubrik"
                      :remote-method="searchRubrik"
                      :remote="true"
                      value-key="name"
                      @change="handleRubrikChange(file, $event)"
                    >
                      <el-option
                        v-for="item in rubrikOptions"
                        :key="item._id || item.name"
                        :label="item.name"
                        :value="item"
                      >
                        <span :style="{ color: item._id ? '#409EFF' : '' }">
                          {{ item._id ? item.name : ` ${item.name}` }}
                        </span>
                      </el-option>
                    </el-select>
                  </td>
                  
                  <td class="align-middle text-center">
                    <button 
                      @click="removeFile(index)"
                      type="button" 
                      :disabled="file.uploading"
                      class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1"
                    >
                      <span class="svg-icon svg-icon-3">
                        <i class="fas fa-trash text-danger"></i>
                      </span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Mobile Card View -->
          <div class="d-md-none">
            <div class="card mb-3" v-for="(file, index) in fileList" :key="file.uid">
              <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <span class="fw-bold">Halaman {{ index + 1 }}</span>
                <button 
                  @click="removeFile(index)"
                  type="button" 
                  :disabled="file.uploading"
                  class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm"
                >
                  <i class="fas fa-trash text-danger"></i>
                </button>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-12 mb-3">
                    <label class="form-label fw-semibold">Nomor Halaman</label>
                    <el-input 
                      v-model="file.page" 
                      placeholder="Contoh: 1, A1, B2"
                      class="w-100"
                    />
                  </div>
                  
                  <div class="col-12 mb-3">
                    <label class="form-label fw-semibold d-block">Gambar</label>
                    <div class="d-flex justify-content-center align-items-center">
                      <img 
                        :src="file.url" 
                        :alt="`Gambar ${file.page}`" 
                        class="img-thumbnail cursor-zoom"
                        style="max-height: 150px; max-width: 100%"
                        @click="openZoomModal(file.url, index)"
                      />
                    </div>
                    <div v-if="file.uploading" class="mt-2">
                      <el-progress 
                        :percentage="file.uploadProgress" 
                        :status="file.uploadError ? 'exception' : ''"
                      />
                      <span v-if="file.uploadError" class="text-danger small">
                        {{ file.uploadError }}
                      </span>
                      <el-button v-if="file.uploadError" @click="uploadFile(file)" size="mini" type="danger" plain
                        class="mt-1 w-100">
                        <i class="el-icon-refresh"></i> Coba Upload Lagi
                      </el-button>
                    </div>
                  </div>
                  
                  <div class="col-12">
                    <label class="form-label fw-semibold">Rubrik</label>
                    <el-select 
                      v-model="file.rubrik" 
                      placeholder="Pilih Rubrik"
                      filterable
                      style="width: 100%"
                      :loading="loadingRubrik"
                      :remote-method="searchRubrik"
                      :remote="true"
                      value-key="name"
                      @change="handleRubrikChange(file, $event)"
                    >
                      <el-option
                        v-for="item in rubrikOptions"
                        :key="item._id || item.name"
                        :label="item.name"
                        :value="item"
                      >
                        <span :style="{ color: item._id ? '#409EFF' : 'black' }">
                          {{ item._id ? item.name : ` ${item.name}` }}
                        </span>
                      </el-option>
                    </el-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-4 d-flex flex-column flex-md-row gap-3 justify-content-between">
          <div>
            <el-button @click="confirmReset" class="w-100 w-md-auto" plain>Reset Form</el-button>
          </div>
          <div>
            <el-button 
              type="primary" 
              @click="submitForm" 
              :disabled="fileList.length === 0 || !form.account || isUploading"
              :loading="isSubmitting"
              class="w-100 w-md-auto"
            >
              <i class="el-icon-upload"></i> Simpan Data ({{ fileList.length }} Halaman)
            </el-button>
          </div>
        </div>
      </div>
    </el-form>

    <!-- Zoom Modal -->
    <el-dialog 
      v-model="zoomModalVisible" 
      title="Preview Gambar" 
      :fullscreen="isZoomed"
    >
      <div class="d-flex justify-content-center align-items-center h-100">
        <img 
          :src="currentZoomImage" 
          class="zoomable-image"
          :class="{ 'zoomed': isZoomed }"
          @click="toggleZoom"
          style="max-width: 100%; max-height: 80vh; object-fit: contain;"
        />
      </div>
      
      <div v-if="fileList.length > 1" class="navigation-controls mt-3">
        <el-button 
          :disabled="currentZoomIndex === 0" 
          @click="prevImage"
          circle
          icon="el-icon-arrow-left"
        />
        <span class="mx-3">{{ currentZoomIndex + 1 }} / {{ fileList.length }}</span>
        <el-button 
          :disabled="currentZoomIndex === fileList.length - 1" 
          @click="nextImage"
          circle
          icon="el-icon-arrow-right"
        />
      </div>

      <template #footer>
        <el-button @click="zoomModalVisible = false">Tutup</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import ApiService from '@/core/services/ApiService';
import { ElMessage, ElMessageBox } from 'element-plus';
import Swal from "sweetalert2/dist/sweetalert2.js";
import { useRouter } from 'vue-router';
import moment from "moment";

export default {
  props: {
    initialData: {
      type: Object,
      default: () => ({
        account: null,
        date: null,
        files: []
      })
    }
  },
  emits: ['submit'],
  setup(props, { emit }) {
    // Reactive state
    const form = ref({
      account: props.initialData.account || null,
      date: props.initialData.date || null,
    });

    const mediaOptions = ref([]);
    const rubrikOptions = ref([]);
    const fileList = ref([]);
    const loadingMedia = ref(false);
    const loadingRubrik = ref(false);
    const isSubmitting = ref(false);
    const formRef = ref(null);
    const router = useRouter();
    const isUploadingAll = ref(false);

    // Zoom modal state
    const zoomModalVisible = ref(false);
    const currentZoomImage = ref('');
    const currentZoomIndex = ref(0);
    const isZoomed = ref(false);

    // Computed properties
    const isUploading = computed(() => {
      return fileList.value.some(file => file.uploading) || isUploadingAll.value;
    });

    // Initialize fileList with proper structure
    const initializeFileList = () => {
      fileList.value = props.initialData.files.map(file => ({
        ...file,
        uid: file.uid || generateUID(),
        page: file.page || '',
        rubrik: file.rubrik ? {
          _id: file.rubrik._id || null,
          name: file.rubrik.name
        } : null,
        url: file.url || '',
        uploaded: !!file.url,
        uploading: false,
        uploadProgress: 0,
        uploadError: null,
        removing: false,
        raw: file.raw || null
      }));
    };

    // Generate unique ID for each file
    const generateUID = () => {
      return Date.now().toString(36) + Math.random().toString(36).substring(2);
    };

    // Zoom modal functions
    const openZoomModal = (imageUrl, index) => {
      currentZoomImage.value = imageUrl;
      currentZoomIndex.value = index;
      zoomModalVisible.value = true;
      isZoomed.value = false;
    };

    const toggleZoom = () => {
      isZoomed.value = !isZoomed.value;
    };

    const nextImage = () => {
      if (currentZoomIndex.value < fileList.value.length - 1) {
        currentZoomIndex.value++;
        currentZoomImage.value = fileList.value[currentZoomIndex.value].url;
      }
    };

    const prevImage = () => {
      if (currentZoomIndex.value > 0) {
        currentZoomIndex.value--;
        currentZoomImage.value = fileList.value[currentZoomIndex.value].url;
      }
    };

    // Media search function
    const searchMedia = async (query) => {
      if (query.length > 2) {
        loadingMedia.value = true;
        try {
          const response = await ApiService.get(`/media?media_like=${query}&limit=100&page=1&source=media_cetak`);
          mediaOptions.value = response.data.data;
        } catch (error) {
          console.error('Error fetching media:', error);
          mediaOptions.value = [];
        } finally {
          loadingMedia.value = false;
        }
      } else {
        mediaOptions.value = [];
      }
    };
    
    const searchRubrik = async (query) => {
      if (query && query.trim().length > 0) {
        loadingRubrik.value = true;
        try {
          const response = await ApiService.get(`/options/rubrics?key=${encodeURIComponent(query)}&limit=5&page=1`);
          rubrikOptions.value = response.data.rubrics || [];
          
          // Add new option if no exact match found
          if (!rubrikOptions.value.some(r => r.name.toLowerCase() === query.toLowerCase())) {
            rubrikOptions.value.push({ name: query });
          }
        } catch (error) {
          console.error('Error searching rubrik:', error);
          rubrikOptions.value = [{ name: query }];
        } finally {
          loadingRubrik.value = false;
        }
      }
    };

    const handleRubrikChange = (file, selectedRubrik) => {
      if (typeof selectedRubrik === 'string') {
        file.rubrik = { name: selectedRubrik };
      } else {
        file.rubrik = selectedRubrik;
      }
    };
    
    const formatDate = (date) => {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    };

    const beforeUpload = (file) => {
      const isImage = file.type.startsWith('image/');
      const isLt50M = file.size / 1024 / 1024 < 50;

      if (!isImage) {
        ElMessage.error('File harus berupa gambar!');
        return false;
      }
      if (!isLt50M) {
        ElMessage.error('Ukuran gambar tidak boleh melebihi 50MB!');
        return false;
      }
      return true;
    };

    const handleFileChange = async (file, files) => {
      const newFiles = files
        .filter(newFile => !fileList.value.some(existing =>
          existing.uid === newFile.uid ||
          existing.name === newFile.name
        ))
        .map((newFile, index) => ({
          ...newFile,
          uid: newFile.uid || generateUID(),
          page: (fileList.value.length + index + 1).toString(),
          rubrik: null,
          url: '',
          uploaded: false,
          uploading: false,
          uploadProgress: 0,
          uploadError: null,
          removing: false
        }));

      fileList.value = [...fileList.value, ...newFiles];
      // Upload file secara sequential (satu per satu)
      isUploadingAll.value = true;
      for (const file of newFiles) {
        await uploadFile(file);
      }
      isUploadingAll.value = false;
    };

    const uploadFile = async (file) => {
      const fileIndex = fileList.value.findIndex(f => f.uid === file.uid);
      if (fileIndex === -1) return;

      fileList.value[fileIndex].uploading = true;
      fileList.value[fileIndex].uploadError = null;

      const formData = new FormData();
      formData.append('file', file.raw);

      try {
        const response = await ApiService.post('/streams/uploadfile', formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const currentFileIndex = fileList.value.findIndex(f => f.uid === file.uid);
            if (currentFileIndex !== -1) {
              fileList.value[currentFileIndex].uploadProgress = progress;
            }
          },
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000
        });

        const updatedFileIndex = fileList.value.findIndex(f => f.uid === file.uid);
        if (updatedFileIndex !== -1) {
          fileList.value[updatedFileIndex].url = response.data.file;
          fileList.value[updatedFileIndex].uploaded = true;
          fileList.value[updatedFileIndex].uploading = false;
          fileList.value[updatedFileIndex].uploadProgress = 100;
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        const errorMessage = error.response.data.message || 'Gagal mengupload file';

        const errorFileIndex = fileList.value.findIndex(f => f.uid === file.uid);
        if (errorFileIndex !== -1) {
          fileList.value[errorFileIndex].uploadError = errorMessage;
          fileList.value[errorFileIndex].uploading = false;
        }

        ElMessage.error(`Gagal upload ${file.name}: ${errorMessage}`);
      }
    };

    const retryFailedUploads = async () => {
      const failedFiles = fileList.value.filter(file =>
        !file.uploaded && file.uploadError && file.raw
      );

      if (failedFiles.length === 0) return;

      isUploadingAll.value = true;
      for (const file of failedFiles) {
        await uploadFile(file);
      }
      isUploadingAll.value = false;
    };

    const removeFile = async (index) => {
      const file = fileList.value[index];

      try {
        if (file.uploaded && file.url) {
          try {
            fileList.value[index].removing = true;

            await ApiService.post('/streams/removefile', {
              data: { path: file.url }
            });
          } catch (error) {
            console.error('Error removing file from server:', error);
          }
        }

        fileList.value.splice(index, 1);
        ElMessage.success('File berhasil dihapus');
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error removing file:', error);
          ElMessage.error('Gagal menghapus file');
        }
      }
    };

    const submitForm = async () => {
      try {
        isSubmitting.value = true;
        
        // Validate form
        const isValid = await formRef.value.validate();
        if (!isValid) {
          ElMessage.error('Harap isi semua field yang wajib diisi');
          return;
        }

        // Check for unuploaded files
        const unuploadedFiles = fileList.value.filter(file => !file.uploaded);
        if (unuploadedFiles.length > 0) {
          const hasErrorFiles = unuploadedFiles.some(file => file.uploadError);

          if (hasErrorFiles) {
            ElMessage.error('Beberapa file gagal diupload. Silakan coba upload ulang atau hapus file yang error.');
            return;
          }

          ElMessage.error('Tunggu sampai semua file selesai diupload');
          return;
        }

        // Prepare payload sesuai format yang diharapkan
        const payload = {
          data: {
            account: form.value.account,
            date: formatDate(form.value.date),
            rubrics: fileList.value.map(file => ({
              name: file.rubrik ? file.rubrik.name : '',
              page: file.page,
              file_source: file.url
            }))
          }
        };

        const response = await ApiService.post('/uncropped-print-files/add', payload);
        Swal.fire({
            title: "Media Berhasil Ditambahkan!",
            icon: "success",
            confirmButtonText: "Ok",
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn btn-light-primary",
            },
          });
        emit('submit', response.data);
        router.push({ name: "media-file" });
      } catch (error) {
        console.error('Error submitting form:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Gagal menyimpan data';
        Swal.fire({
          title: "Error",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "Ok",
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-light-primary",
          },
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    const confirmReset = async () => {
      try {
        await ElMessageBox.confirm(
          'Anda yakin ingin mereset form? Semua data akan hilang.',
          'Konfirmasi Reset',
          {
            confirmButtonText: 'Ya, Reset',
            cancelButtonText: 'Batal',
            type: 'warning',
          }
        );

        const removePromises = fileList.value
          .filter(file => file.uploaded && file.url)
          .map(file => 
            ApiService.post('/streams/removefile', {
              data: { path: file.url }
            }).catch(error => {
              console.error('Error removing file:', error);
              return null;
            })
          );

        await Promise.all(removePromises);

        form.value = {
          account: null,
          date: null,
        };
        fileList.value = [];
        mediaOptions.value = [];
        rubrikOptions.value = [];
        ElMessage.success('Form telah direset');
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error resetting form:', error);
        }
      }
    };

    // Initialize on mount
    onMounted(() => {
      initializeFileList();
    });

    // Watch for initialData changes
    watch(() => props.initialData, (newVal) => {
      form.value.account = newVal.account || null;
      form.value.date = newVal.date || null;
      initializeFileList();
    }, { deep: true });

    return {
      form,
      mediaOptions,
      rubrikOptions,
      fileList,
      loadingMedia,
      loadingRubrik,
      isSubmitting,
      formRef,
      isUploading,
      zoomModalVisible,
      currentZoomImage,
      currentZoomIndex,
      isZoomed,
      searchMedia,
      searchRubrik,
      handleRubrikChange,
      handleFileChange,
      removeFile,
      submitForm,
      confirmReset,
      beforeUpload,
      retryFailedUploads,
      uploadFile,
      openZoomModal,
      toggleZoom,
      nextImage,
      prevImage
    };
  }
};
</script>

<style scoped>
.img-thumbnail {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  transition: transform 0.2s;
  cursor: zoom-in;
}

.img-thumbnail:hover {
  transform: scale(1.05);
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.cursor-zoom {
  cursor: zoom-in;
}

/* Zoom modal styles */
.zoomable-image {
  transition: transform 0.3s ease;
  cursor: zoom-in;
}

.zoomable-image.zoomed {
  transform: scale(2);
  cursor: zoom-out;
}

.navigation-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
}

/* Hide default upload list */
:deep(.el-upload-list) {
  display: none;
}

/* Mobile card styles */
@media (max-width: 767.98px) {
  .card-header {
    padding: 0.75rem;
  }
  
  .card-body {
    padding: 1rem;
  }
  
  .img-thumbnail {
    max-height: 120px;
  }
  
  .form-label {
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-responsive {
    max-height: 400px;
  }
  
  .img-thumbnail {
    max-height: 80px;
  }
}

.el-select {
  width: 100%;
}
</style>